import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList, CommandGroup } from "@/components/ui/command";
import { X, Plus, Check, ChevronDown } from "lucide-react";

/**
 * Inline editable combobox. Supports selecting a preset, typing free text,
 * adding a new preset option, and deleting existing options.
 * With `inlineEdit`, the trigger is a real input so the selected value can be
 * typed/edited directly, with a chevron to open the preset list.
 */
export function EditableCombobox({
  field,
  value,
  onChange,
  options = [],
  onAdd,
  onDelete,
  placeholder = "",
  align = "start",
  bare = false,
  center = false,
  inlineEdit = false,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const q = query.trim();
  const filtered = options.filter((o) => o.toLowerCase().includes(q.toLowerCase()));
  const exists = options.some((o) => o.toLowerCase() === q.toLowerCase());

  const select = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  // In inline mode, selecting a preset is COMBINED with the text already typed
  // (e.g. type "45", pick "Yrs/Male" -> "45 Yrs/Male"). Otherwise it replaces.
  const applyOption = (o) => {
    if (inlineEdit && value && value.trim()) select(`${value.trim()} ${o}`);
    else select(o);
  };

  const optionsList = (
    <Command shouldFilter={false}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search or type..."
        data-testid={`${testid}-search`}
        onKeyDown={(e) => {
          if (e.key === "Enter" && q) {
            e.preventDefault();
            applyOption(q);
          }
        }}
      />
      <CommandList className="max-h-56">
        {q && !exists && (
          <CommandGroup heading="New">
            <CommandItem value={`use-${q}`} onSelect={() => applyOption(q)} data-testid={`${testid}-use`}>
              <Check className="mr-2 h-4 w-4 text-sky-600" /> {inlineEdit && value && value.trim() ? `Add “${q}”` : `Use “${q}”`}
            </CommandItem>
            <CommandItem
              value={`add-${q}`}
              onSelect={() => {
                onAdd(field, q);
                applyOption(q);
              }}
              data-testid={`${testid}-add`}
            >
              <Plus className="mr-2 h-4 w-4 text-sky-600" /> Add “{q}” to list
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup heading="Options">
          {filtered.map((o) => (
            <CommandItem
              key={o}
              value={o}
              onSelect={() => applyOption(o)}
              className="flex items-center justify-between group"
            >
              <span className="flex items-center">
                {value === o && <Check className="mr-2 h-4 w-4 text-sky-600" />}
                {o}
              </span>
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-0.5"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(field, o);
                }}
                data-testid={`${testid}-del-${o}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </CommandItem>
          ))}
          {filtered.length === 0 && !q && (
            <div className="px-3 py-2 text-xs text-slate-400">No options yet — type to add one.</div>
          )}
        </CommandGroup>
        {value && (
          <CommandGroup>
            <CommandItem value="__clear" onSelect={() => select("")} className="text-slate-500">
              <X className="mr-2 h-4 w-4" /> Clear field
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );

  if (inlineEdit) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative flex items-center w-full">
          <input
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`combo-underline font-paper flex-1 min-w-0 text-[15px] bg-transparent outline-none ${!value ? "combo-placeholder" : ""}`}
            style={{ fontSize: "inherit", fontFamily: "inherit" }}
          />
          <PopoverTrigger asChild>
            <button type="button" data-testid={`${testid}-toggle`} className="ml-0.5 shrink-0 text-slate-400 hover:text-sky-600 no-print">
              <ChevronDown className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent align={align} className="p-0 w-72 bg-white border shadow-lg z-50" data-testid={`${testid}-popover`}>
          {optionsList}
        </PopoverContent>
      </Popover>
    );
  }

  const triggerCls = bare
    ? `block w-full px-2 py-1.5 text-[13px] cursor-pointer hover:bg-sky-50 rounded-sm ${center ? "text-center" : ""} ${!value ? "min-h-[1.4em]" : ""}`
    : `combo-underline font-paper w-full text-[15px] ${!value ? "combo-placeholder" : ""}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span data-testid={testid} className={triggerCls}>
          {value || (bare ? "" : placeholder)}
        </span>
      </PopoverTrigger>
      <PopoverContent align={align} className="p-0 w-72 bg-white border shadow-lg z-50" data-testid={`${testid}-popover`}>
        {optionsList}
      </PopoverContent>
    </Popover>
  );
}
